import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ForkKnife, Plus, Minus, House, ArrowLeft, CheckCircle, ShoppingCart, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PhosphorIcon } from "@/components/PhosphorIcon";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import ThemeToggle from "@/components/ThemeToggle";
import BrandLogo from "@/components/BrandLogo";
import { playEventSound, setSoundConfig } from "@/lib/sound";

/**
 * One row inside a half/full item card on the order screen — shows the portion
 * label + price and either an Add button or a +/- quantity stepper. Kept tiny
 * and inline because it's only used here.
 */
function PortionRow({ label, price, qty, onAdd, onRemove, testid }) {
    return (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2" data-testid={`${testid}-row`}>
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">{label}</span>
                <span className="font-mono text-sm text-gray-900 dark:text-gray-100">₹ {Number(price || 0).toFixed(0)}</span>
            </div>
            {qty === 0 ? (
                <Button size="sm" onClick={onAdd} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid={`${testid}-add-btn`}>
                    <Plus size={14} className="mr-1" /> Add
                </Button>
            ) : (
                <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" onClick={onRemove} className="h-8 w-8" data-testid={`${testid}-minus-btn`}><Minus size={14} /></Button>
                    <span className="font-mono w-6 text-center font-semibold" data-testid={`${testid}-qty`}>{qty}</span>
                    <Button size="icon" variant="outline" onClick={onAdd} className="h-8 w-8" data-testid={`${testid}-plus-btn`}><Plus size={14} /></Button>
                </div>
            )}
        </div>
    );
}

export default function RoomOrder() {
    const { pin } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const [room, setRoom] = useState(null);
    const [menu, setMenu] = useState([]);
    const [cart, setCart] = useState({}); // key: `${id}::full` or `${id}::half` → qty
    const [busy, setBusy] = useState(false);
    // Active category tab (mirrors the Tap-to-Request kiosk UX). null until
    // the menu loads, then set to the first category that has items.
    const [activeCategory, setActiveCategory] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.post("/rooms/access", { pin });
                // Server-side enforcement also blocks the POST, but redirect
                // here so the user never sees the menu when food orders are
                // disabled — even if they typed the URL directly.
                if (data.food_orders_enabled === false) {
                    toast.error("Food orders are disabled for this room.");
                    navigate(`/room/${pin}`, { replace: true });
                    return;
                }
                setRoom(data);
            } catch {
                toast.error("Invalid PIN");
                navigate("/");
            }
        })();
    }, [pin, navigate]);

    useEffect(() => {
        if (!room) return;
        (async () => {
            try {
                const { data } = await api.get("/settings/sounds", { params: { location_id: room.location_id } });
                const map = Object.fromEntries(
                    Object.entries(data || {}).filter(([_, v]) => v).map(([k, v]) => [k.replace(/^sound_/, ""), v])
                );
                if (Object.keys(map).length) setSoundConfig(map);
            } catch { /* sound config is best-effort */ }
        })();
    }, [room]);

    useEffect(() => {
        if (!room) return;
        (async () => {
            const { data } = await api.get("/menu", {
                params: { available_only: true, location_id: room.location_id },
            });
            setMenu(data);
        })();
    }, [room]);

    const groups = useMemo(() => Array.from(new Set(menu.map((m) => m.category))), [menu]);
    // Default the active category to the first group as soon as the menu loads.
    // Also recover when the previously-active category no longer exists (e.g.
    // admin renamed/removed a category between sessions).
    useEffect(() => {
        if (!groups.length) return;
        if (!activeCategory || !groups.includes(activeCategory)) {
            setActiveCategory(groups[0]);
        }
    }, [groups, activeCategory]);
    // Resolve a cart key (`${id}::full` / `${id}::half`) back to {item, portion, unitPrice}.
    const resolveCartLine = (key) => {
        const [id, portion = "full"] = key.split("::");
        const item = menu.find((m) => m.id === id);
        if (!item) return null;
        const unitPrice = portion === "half" && item.half_price != null ? Number(item.half_price) : Number(item.price || 0);
        return { item, portion, unitPrice };
    };
    const total = useMemo(() => {
        return Object.entries(cart).reduce((sum, [key, qty]) => {
            const line = resolveCartLine(key);
            return sum + (line ? line.unitPrice * qty : 0);
        }, 0);
    }, [cart, menu]);

    const add = (id, portion = "full") => setCart((c) => {
        const key = `${id}::${portion}`;
        return { ...c, [key]: (c[key] || 0) + 1 };
    });
    const remove = (id, portion = "full") =>
        setCart((c) => {
            const key = `${id}::${portion}`;
            const q = (c[key] || 0) - 1;
            const next = { ...c };
            if (q <= 0) delete next[key];
            else next[key] = q;
            return next;
        });

    const placeOrder = async () => {
        if (!room || Object.keys(cart).length === 0) return;
        setBusy(true);
        try {
            const items = Object.entries(cart).map(([key, qty]) => {
                const line = resolveCartLine(key);
                if (!line) return null;
                const { item, portion, unitPrice } = line;
                // Include portion in the line name so kitchen + staff dashboards
                // see e.g. "Veg Biryani (Half)" without any code changes there.
                const displayName = portion === "half" ? `${item.name} (Half)` : item.name;
                return { menu_item_id: item.id, name: displayName, portion, qty, price: unitPrice };
            }).filter(Boolean);
            await api.post("/preorders", { room_id: room.id, pin, items });
            playEventSound("new_order");
            toast.success("Order placed!");
            setCart({});
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        } finally {
            setBusy(false);
        }
    };

    if (!room) return <div className="min-h-screen grid place-items-center text-gray-400">Loading…</div>;

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0D10] flex flex-col">
            <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0B0D10]">
                <div className="max-w-[1400px] mx-auto px-8 pt-5 pb-2 flex items-center justify-between">
                    <BrandLogo height={32} />
                    <div className="flex items-center gap-3">
                        <LanguageToggle />
                        <ThemeToggle />
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto px-8 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" onClick={() => navigate(`/room/${pin}`)} className="text-gray-500" data-testid="order-back-btn">
                            <ArrowLeft size={18} className="mr-1" /> Back
                        </Button>
                        <div>
                            <div className="label-eyebrow text-gray-500">{room.name}</div>
                            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                {t("kiosk.mode_order")}
                            </h1>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-[1400px] mx-auto px-8 py-8 w-full grid lg:grid-cols-3 gap-8">
                {/* Menu */}
                <div className="lg:col-span-2">
                    {groups.length === 0 ? (
                        <div className="text-sm text-gray-500 py-12 text-center">No menu items available right now.</div>
                    ) : (
                        <Tabs value={activeCategory || ""} onValueChange={setActiveCategory} className="w-full">
                            {/* Category tab strip — mirrors the Tap-to-Request UX */}
                            <TabsList className="bg-gray-100 dark:bg-gray-900/40 p-1 rounded-lg flex flex-wrap h-auto justify-start" data-testid="order-category-tabs">
                                {groups.map((g) => (
                                    <TabsTrigger
                                        key={g}
                                        value={g}
                                        className="px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-[#111418]"
                                        data-testid={`order-cat-tab-${g.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {g}
                                    </TabsTrigger>
                                ))}
                            </TabsList>

                            <TabsContent value={activeCategory || ""} className="mt-6">
                                <div className="grid sm:grid-cols-2 gap-3" data-testid="order-items-grid">
                                    {menu.filter((m) => m.category === activeCategory).map((m) => {
                                        const hasHalf = m.half_price != null && Number(m.half_price) > 0;
                                        const fullKey = `${m.id}::full`;
                                        const halfKey = `${m.id}::half`;
                                        const fullQty = cart[fullKey] || 0;
                                        const halfQty = cart[halfKey] || 0;
                                        return (
                                            <Card key={m.id} className="p-4 bg-white dark:bg-[#111418] border-gray-200 dark:border-gray-800 flex flex-col gap-3" data-testid={`menu-item-${m.id}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 rounded-lg grid place-items-center" style={{ backgroundColor: `${m.color}15` }}>
                                                        <PhosphorIcon name={m.icon} size={26} weight="duotone" color={m.color} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 dark:text-gray-100">{m.name}</div>
                                                        <div className="text-xs text-gray-500 truncate">{m.description}</div>
                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                                                            {hasHalf ? (
                                                                <>
                                                                    <span className="font-mono text-sm text-gray-700 dark:text-gray-300">Full ₹{Number(m.price || 0).toFixed(0)}</span>
                                                                    <span className="font-mono text-sm text-gray-700 dark:text-gray-300">· Half ₹{Number(m.half_price).toFixed(0)}</span>
                                                                </>
                                                            ) : (
                                                                <span className="font-mono text-sm text-gray-700 dark:text-gray-300">₹ {Number(m.price || 0).toFixed(0)}</span>
                                                            )}
                                                            {m.quantity ? (
                                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">{m.quantity}</span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                                {hasHalf ? (
                                                    // Two portion rows so the user explicitly picks Full or Half.
                                                    <div className="flex flex-col gap-2">
                                                        <PortionRow
                                                            label="Full"
                                                            price={Number(m.price || 0)}
                                                            qty={fullQty}
                                                            onAdd={() => add(m.id, "full")}
                                                            onRemove={() => remove(m.id, "full")}
                                                            testid={`menu-${m.id}-full`}
                                                        />
                                                        <PortionRow
                                                            label="Half"
                                                            price={Number(m.half_price)}
                                                            qty={halfQty}
                                                            onAdd={() => add(m.id, "half")}
                                                            onRemove={() => remove(m.id, "half")}
                                                            testid={`menu-${m.id}-half`}
                                                        />
                                                    </div>
                                                ) : (
                                                    // Original single Add → quantity stepper for plain items.
                                                    <div className="self-end">
                                                        {fullQty === 0 ? (
                                                            <Button size="sm" onClick={() => add(m.id, "full")} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid={`menu-${m.id}-add-btn`}>
                                                                <Plus size={14} className="mr-1" /> Add
                                                            </Button>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <Button size="icon" variant="outline" onClick={() => remove(m.id, "full")} className="h-8 w-8"><Minus size={14} /></Button>
                                                                <span className="font-mono w-6 text-center font-semibold">{fullQty}</span>
                                                                <Button size="icon" variant="outline" onClick={() => add(m.id, "full")} className="h-8 w-8"><Plus size={14} /></Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Card>
                                        );
                                    })}
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}
                </div>

                {/* Cart */}
                <aside className="lg:sticky lg:top-8 self-start">
                    <Card className="p-5 bg-white dark:bg-[#111418] border-gray-200 dark:border-gray-800">
                        <div className="flex items-center gap-2 mb-4">
                            <ShoppingCart size={20} weight="duotone" color="#0055FF" />
                            <div className="font-display font-semibold text-lg text-gray-900 dark:text-gray-100">{t("kiosk.cart")}</div>
                            <Badge className="ml-auto bg-blue-50 text-blue-700 border border-blue-200">{Object.values(cart).reduce((a, b) => a + b, 0)} items</Badge>
                        </div>
                        {Object.keys(cart).length === 0 ? (
                            <div className="text-sm text-gray-500 py-8 text-center">{t("kiosk.empty_cart")}</div>
                        ) : (
                            <>
                                <ul className="divide-y divide-gray-100 dark:divide-gray-800 mb-4">
                                    {Object.entries(cart).map(([key, qty]) => {
                                        const line = resolveCartLine(key);
                                        if (!line) return null;
                                        const { item, portion, unitPrice } = line;
                                        const label = portion === "half" ? `${item.name} (Half)` : item.name;
                                        return (
                                            <li key={key} className="flex items-center gap-2 py-2 text-sm">
                                                <span className="flex-1 text-gray-700 dark:text-gray-300">{label}</span>
                                                <span className="font-mono text-gray-500">×{qty}</span>
                                                <span className="font-mono font-medium w-16 text-right text-gray-900 dark:text-gray-100">₹{(unitPrice * qty).toFixed(0)}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-800">
                                    <span className="label-eyebrow">Total</span>
                                    <span className="font-display font-bold text-2xl text-gray-900 dark:text-gray-100">₹{total.toFixed(0)}</span>
                                </div>
                                <Button disabled={busy} onClick={placeOrder} className="w-full mt-4 h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="order-place-btn">
                                    {busy ? "Placing…" : t("kiosk.checkout")} <CheckCircle size={16} className="ml-1" />
                                </Button>
                            </>
                        )}
                    </Card>
                </aside>
            </main>
        </div>
    );
}
